"use strict";

// ── THE CLAIM PAGE'S ONE ACREAGE ANSWER, PER COUNTY ──────────────────────────────────────
//
// Kevin, 2026-10-26, on RED DAWN FARMING LLC's corn claim: the page said three different
// things about how many planted acres had production ("3,380 of 5,747", "5,294.5 (92%)",
// "3,253.4 matched") and printed "-1,961.6 not covered", a NEGATIVE acreage.
//
// Every one of those figures was a different subtraction. This module is the replacement and
// it does NO arithmetic of its own about what accounts for an acre: each county row is a call
// to `acreAccounting()` (src/claims/fieldCoverage.js) and the table is that rule's `segments`
// regrouped into the four columns a reader acts on. The parts sum to the planted acres by
// construction, and are asserted to by tools/check-claim-page.js.
//
// ⚠ IF THE COUNTIES DO NOT RECONCILE, THE PAGE FALLS BACK TO ONE FARM-LEVEL ROW, AND SAYS SO.
// A field row carries the county the FSA header names (and, additively, `insuredCounty`),
// while the coverage rows carry the county the INSURANCE names. Those agree on most farms and
// not on all. A table that quietly dropped or double-counted a mismatched field would be a
// fourth acreage answer — so the per-county view is only returned when the county rows add
// back to the farm's own planted acres.

const { acreAccounting } = require("./fieldCoverage.js");

const r1 = (v) => +((Number(v) || 0)).toFixed(1);
const up = (s) => String(s == null ? "" : s).trim().toUpperCase();
const sumBy = (rows, pred, f) => rows.filter(pred).reduce((a, r) => a + (f(r) || 0), 0);

// The same per-state sums buildFieldCoverage puts in `totals`, over a SUBSET of the rows.
// `stateAcres` is the part of a field counted under its state (a partly harvested field's
// machine-covered acres, fieldCoverage partialHarvest); the rest is `uncoveredAcres`.
const stAc = (r) => (r.stateAcres != null ? r.stateAcres : r.insuredAcres);
const unAc = (r) => r.uncoveredAcres || 0;
// A grain field the combine and the chopper both crossed carries its second part in `splitPart`
// (fieldCoverage, partialHarvest): its acres count under THAT part's state, never twice.
const partAc = (r, s) => (r.splitPart && r.splitPart.state === s ? r.splitPart.acres || 0 : 0);
const inStates = (r, states) => states.includes(r.state) || !!(r.splitPart && states.includes(r.splitPart.state));
function totalsOf(rows) {
  const ac = (s) => r1(sumBy(rows, (r) => r.state === s, stAc) + sumBy(rows, (r) => !!r.splitPart, (r) => partAc(r, s)));
  return {
    insuredAcres: r1(sumBy(rows, () => true, (r) => r.insuredAcres)),
    // A part-prevent-plant field on the claim page counts its planted acres under its state; its PP
    // acres are prevent plant (fieldCoverage `plantedBasis`).
    preventPlantAcres: r1(ac("pp") + sumBy(rows, (r) => r.plantedBasis && r.state !== "pp", (r) => r.ppAcres || 0)),
    appraisedOnlyAcres: ac("appraised"), harvestedOnlyAcres: ac("harvested"), bothAcres: ac("both"),
    bothChoppedAcres: ac("both_chopped"), bothUnknownAcres: ac("both_unknown"),
    neitherAcres: ac("neither"), unmappedAcres: ac("unmapped"),
    choppedAcres: ac("chopped"), harvestUnknownAcres: ac("harvest_unknown"),
    silageChoppedAcres: ac("silage_chopped"),
    silageNoneAcres: r1(ac("silage_none") + sumBy(rows, (r) => r.state === "silage_chopped", unAc)),
    partialUncoveredAcres: r1(sumBy(rows, (r) => r.state !== "silage_chopped", unAc)),
  };
}

// ⚠ INSURED-AS-SILAGE ACRES ARE THEIR OWN LINE, IN TONS, AND NO FIGURE HERE IS EVER ADDED TO A
// BUSHEL (Kevin: "we never merge those"; 7 CFR 457.113 §12(b) — grain and silage are separate
// types, each counted at its own price; no tons-to-bushels conversion exists in the policy).
//   estAc / wetTons / dmTons  the JD chopper estimate on chopped silage-insured fields — wet tons
//                             at 35% DM (a unit conversion of John Deere's dry matter). A
//                             SCREENING figure, never production to count (LASH ¶14).
//   noneAc                    silage-insured acres with no silage figure yet.
//   buAppr                    bushel appraisals sitting on silage-insured fields: the wrong unit
//                             (Kevin, 2026-11-03), shown as written and flagged, never counted.
//                             They may be a grain-content test for grain-deficient silage
//                             (457.113 §12(e)(1)) rather than production — that call is Kevin's.
function silageOf(rows) {
  const sil = rows.filter((r) => r.state === "silage_chopped" || r.state === "silage_none");
  const est = sil.filter((r) => r.silageEstimate);
  const wrong = sil.filter((r) => r.wrongUnitAppraisal && r.appraised);
  return {
    estAc: r1(sumBy(sil, (r) => r.state === "silage_chopped", stAc)),
    wetTons: Math.round(sumBy(est, () => true, (r) => r.silageEstimate.wetTons)),
    dmTons: Math.round(sumBy(est, () => true, (r) => r.silageEstimate.dmTons)),
    machineAc: r1(sumBy(est, () => true, (r) => r.silageEstimate.acres)),
    noneAc: r1(sumBy(sil, (r) => r.state === "silage_none", stAc)
      + sumBy(sil, (r) => r.state === "silage_chopped", unAc)),
    buAppr: {
      fields: wrong.length,
      acres: r1(sumBy(wrong, () => true, (r) => r.appraised.acres)),
      bu: Math.round(sumBy(wrong, () => true, (r) => r.appraised.quantity)),
    },
  };
}

// Summed on the unrounded figure (fieldCoverage `quantityExact`) and rounded once.
const qtyOf = (x) => (x.quantityExact != null ? x.quantityExact : x.quantity);
// The combine figure of a row: on a mixed field only the combine's own part (`combinePart`), never
// the chopper's dry matter that rides in `harvested` beside it.
const combineFig = (r) => r.combinePart || r.harvested;
const figOf = (r, side) => (side === "harvested" ? combineFig(r) : r[side]);
// A row is summed once per call: its own state or its second part's, whichever is listed.
const bushelsOf = (rows, states, side, alloc) =>
  Math.round(sumBy(rows, (r) => inStates(r, states) && figOf(r, side), (r) => qtyOf(figOf(r, side)))
    + (side === "appraised" && alloc ? sumBy(rows, (r) => inStates(r, states), (r) => alloc.get(r)) : 0));

// ⚠ A WORKSHEET LINE THAT COVERS SEVERAL FIELDS CARRIES ITS BUSHELS AT THE FARM / UNIT, not per
// field (fieldCoverage: farmLevelAppraisals). Those fields' ACRES are accounted for, so their
// bushels must be too — otherwise they read as "appraisal on file but not tied to a field"
// (Red Dawn: three unit-level lines, 44,294 bu). The line's quantity is exact; only WHICH column
// it lands in (standing vs chopped) is apportioned by planted acres across the fields it covers.
// ⚠ ONLY ONTO THE FIELDS THAT LINE'S OWN RECONCILIATION COVERED — its farm number, unit and type
// (`group`, set by fieldCoverage). Matching "unit OR farm number" spread WALKER FARM's unit-3
// line on FSN 6721 into unit 2 (Steuben −353 / Livingston +355). An entry without a group (an
// older caller) keeps the old reach.
function allocateFarmLevel(fcRows, farmLevel) {
  const alloc = new Map();
  for (const en of farmLevel || []) {
    if (!(en.quantity > 0)) continue;
    if (en.typeClass === "silage") continue;   // flag-only, never allocated
    const t = fcRows.filter((r) => r.appraised && r.appraised.atFarmLevel
      && (en.group ? r.appraised.group === en.group
        : ((en.unitKey && r.unitKey === en.unitKey) || (en.fsn && r.fsn === en.fsn))));
    const w = t.map((r) => Math.max(0, r.insuredAcres - (r.ppAcres || 0)));
    const tot = w.reduce((a, b) => a + b, 0);
    if (!t.length || !(tot > 0)) continue;
    t.forEach((r, i) => alloc.set(r, (alloc.get(r) || 0) + en.quantity * (w[i] / tot)));
  }
  return alloc;
}

// ── ONE FIELD'S PRODUCTION TO COUNT, AND THE ACRES IT STANDS FOR ──────────────────────────
// The per-row half of countyLine's `toCountBu`: the same three bushelsOf calls (combine figure on
// harvested / both; the appraisal on chopped; the appraisal on standing ground, with any
// farm-level worksheet share in `alloc`), over ONE row and unrounded, so a caller that regroups
// the rows (unitYield.js, by insurance unit) adds back to the county card. `acres` are the
// planted acres those bushels speak for: each counted state's own acres, never a partly harvested
// field's remainder (its `uncoveredAcres`), never a silage-insured acre (tons, not bushels).
const COUNTED_STATES = ["harvested", "both", "both_chopped", "both_unknown", "appraised"];
function rowProduction(r, alloc) {
  const one = (states, side) => (inStates(r, states) && figOf(r, side) ? qtyOf(figOf(r, side)) : 0)
    + (side === "appraised" && alloc && inStates(r, states) ? (alloc.get(r) || 0) : 0);
  const bu = one(["harvested", "both"], "harvested") + one(["both_chopped", "both_unknown"], "appraised")
    + one(["appraised"], "appraised");
  const acres = (COUNTED_STATES.includes(r.state) ? stAc(r) : 0)
    + (r.splitPart && COUNTED_STATES.includes(r.splitPart.state) ? partAc(r, r.splitPart.state) : 0);
  const source = inStates(r, ["harvested", "both"]) ? (inStates(r, ["both_chopped", "both_unknown"]) ? "combine + appraisal" : "combine")
    : inStates(r, ["both_chopped", "both_unknown", "appraised"]) ? "appraisal" : null;
  return { bu, acres, source };
}

/**
 * One county's row: planted acres split into appraised / combined / chopped→appraisal /
 * nothing yet, with the bushels behind each, all from acreAccounting().
 */
function countyLine({ county, rows, policyInsured, policyPp, countyAppraised, countyAppraisedBu, alloc, unplaced = [] }) {
  const t = totalsOf(rows);
  const acc = acreAccounting({ totals: t, policyInsured, policyPp, countyAppraised });
  let appraised = r1(t.appraisedOnlyAcres + acc.apprNotPlaced);
  let combined = r1(t.harvestedOnlyAcres + t.bothAcres);
  let chopped = r1(t.bothChoppedAcres + t.bothUnknownAcres);
  const silage = silageOf(rows);
  // acreAccounting caps `accounted` at planted; the columns are the uncapped parts, so
  // take any excess back out (appraised first — it is the weakest evidence) to keep the
  // identity. The excess is REPORTED as `over`, never absorbed silently.
  let over = acc.overAccounted;
  for (const take of ["appraised", "chopped", "combined", "silage"]) {
    if (over <= 0.05) break;
    const have = take === "appraised" ? appraised : take === "chopped" ? chopped : take === "combined" ? combined : silage.estAc;
    const cut = Math.min(have, over);
    if (take === "appraised") appraised = r1(appraised - cut);
    else if (take === "chopped") chopped = r1(chopped - cut);
    else if (take === "combined") combined = r1(combined - cut);
    else silage.estAc = r1(silage.estAc - cut);
    over = r1(over - cut);
  }
  // "Nothing yet" splits by type so the grain rows and the silage rows each add up. The
  // silage share is its own fields' acres; any acres the map does not carry stay grain-side.
  const silageNothing = r1(Math.min(acc.stillNeed, silage.noneAc));
  silage.nothing = silageNothing;
  // ⚠ A GRAIN LINE THAT NAMES NO FIELD IS STILL PRODUCTION OF ITS UNIT (7 CFR 457.113; Corn LASH
  // FCIC-25080), so its bushels COUNT here, in the county its unit sits in, on their own row —
  // "appraised, field not identified" — never on a guessed field. Its acres stand for grain
  // ground with no field figure, but never more than the county's appraisal acres not already
  // drawn on the map (acreAccounting's apprUnusable): a line placed on PART of a field already
  // counts the whole field, and the unit row must not count that ground again (ROGERS: 308.0
  // appraised ac on file, 293.4 field ac drawn for 265.6 of them). KINGSLEY 22,944 bu, RED DAWN 21,168, ROGERS
  // 8,470, ANCHOR 5,931 were shown as a note and left out of production to count.
  const unplacedBu = Math.round(unplaced.reduce((a, u) => a + (Number(u.quantity) || 0), 0));
  const unplacedAc = r1(unplaced.reduce((a, u) => a + (Number(u.acres) || 0), 0));
  const grainNeed = r1(acc.stillNeed - silageNothing);
  const apprUnplacedAc = r1(Math.max(0, Math.min(unplacedAc, acc.apprUnusable, grainNeed)));
  const matchedApprBu = bushelsOf(rows, ["appraised", "both_chopped", "both_unknown", "both"], "appraised", alloc);
  // Appraisal on file for this county that neither a field nor the unit row took: stated apart.
  const unmatchedAc = r1(Math.max(0, acc.apprUnusable - apprUnplacedAc));
  const unmatchedApprBu = acc.apprBeyondMap > 0.05 && countyAppraisedBu != null
    ? Math.max(0, Math.round(countyAppraisedBu) - matchedApprBu - unplacedBu) : 0;
  // High-moisture-corn fields (a stored human decision) are chopped-valued; split out for display only.
  // Both parts stay inside `chopped` / `bu.chopped`, so production to count is unchanged.
  const hmcRows = rows.filter((r) => r.hmcMarked && r.state === "both_chopped");
  const hmcAc = r1(sumBy(hmcRows, () => true, (r) => r.insuredAcres));
  return {
    county,
    hmcAc: Math.min(hmcAc, r1(t.bothChoppedAcres + t.bothUnknownAcres)),
    planted: acc.denom,
    appraised, combined, chopped,
    // Unit-level appraisal acres (field not identified), taken out of "nothing yet".
    appraisedUnplaced: apprUnplacedAc,
    nothing: r1(acc.stillNeed - apprUnplacedAc),
    // The grain side of `nothing`, and the silage line. planted = appraised + combined +
    // chopped + appraisedUnplaced + grainNothing + silage.estAc + silage.nothing.
    grainNothing: r1(grainNeed - apprUnplacedAc),
    silage,
    unplacedLines: unplaced.length,
    toCountBu: bushelsOf(rows, ["harvested", "both"], "harvested") + bushelsOf(rows, ["both_chopped", "both_unknown"], "appraised", alloc)
      + bushelsOf(rows, ["appraised"], "appraised", alloc) + unplacedBu,
    bu: {
      appraised: bushelsOf(rows, ["appraised"], "appraised", alloc),
      appraisedUnplaced: unplacedBu,
      unmatched: unmatchedApprBu,
      hmc: bushelsOf(hmcRows, ["both_chopped"], "appraised", alloc),
      combined: bushelsOf(rows, ["harvested", "both"], "harvested"),
      chopped: bushelsOf(rows, ["both_chopped", "both_unknown"], "appraised", alloc),
    },
    unmatchedAc,
    // What the "nothing yet" acres are, for the to-do list.
    gaps: {
      neither: t.neitherAcres, unmapped: t.unmappedAcres, chopped: t.choppedAcres,
      harvestUnknown: t.harvestUnknownAcres, noField: acc.noFieldNoAppraisal,
      partial: t.partialUncoveredAcres,
    },
    // Appraisal acres on file that are tied to nothing at all, after the unit row took its share.
    apprUnusable: unmatchedAc,
    acc,
  };
}

/**
 * claimAccountingView({ fcRows, counties, farm })
 *
 * `fcRows`   buildFieldCoverage().rows
 * `counties` productionEvidence's `counties` (the policy's own county rows)
 * `farm`     the farm-level acreAccounting() answer — used for the fallback and to reconcile
 *
 * Returns { mode: "county" | "farm", lines, total } where `total` is the sum of `lines` and
 * is the ONE answer the headline, the to-do list and the yield gate all read.
 */
function claimAccountingView({ fcRows = [], counties = [], farm, farmLevel = [], unplaced = [] }) {
  const alloc = allocateFarmLevel(fcRows, farmLevel);
  // Grain lines tied to no field, by the county their unit sits in (fieldCoverage countyOfLine).
  // Bushel lines on silage-insured ground never reach here (fieldCoverage unplacedSilageBu).
  const grainUnplaced = (unplaced || []).filter((u) => u && u.typeClass !== "silage");
  const unplacedIn = (c) => grainUnplaced.filter((u) => up(u.countyOfLine || u.county) === c);
  const byCounty = new Map();
  for (const r of fcRows) {
    const k = up(r.insuredCounty) || up(r.county) || "(county not stated)";
    if (!byCounty.has(k)) byCounty.set(k, []);
    byCounty.get(k).push(r);
  }
  const known = new Set(counties.map((c) => up(c.county)));
  const strays = [...byCounty.keys()].filter((k) => !known.has(k));

  const farmTotals = totalsOf(fcRows);
  const farmLine = () => countyLine({
    county: "All counties", rows: fcRows,
    policyInsured: farm.policyInsured, policyPp: farm.pp,
    countyAppraised: farm.countyAppraised,
    countyAppraisedBu: counties.reduce((a, c) => a + ((c.appraised && c.appraised.quantity) || 0), 0),
    alloc, unplaced: grainUnplaced,
  });

  let mode = "county";
  let lines = [];
  if (counties.length > 1 && !strays.length) {
    lines = counties.map((c) => countyLine({
      county: up(c.county), rows: byCounty.get(up(c.county)) || [],
      policyInsured: c.insuredAcres, policyPp: c.ppAcres,
      countyAppraised: (c.appraised && c.appraised.acres) || 0,
      countyAppraisedBu: (c.appraised && c.appraised.quantity) || 0,
      alloc, unplaced: unplacedIn(up(c.county)),
    }));
    const planted = r1(lines.reduce((a, l) => a + l.planted, 0));
    if (Math.abs(planted - r1(farm.denom)) > 0.15) mode = "farm";
  } else mode = "farm";
  if (mode === "farm") lines = [farmLine()];
  // A line whose unit's county is not one of the policy's counties: counted at the FARM, flagged.
  const placedSet = new Set(lines.flatMap((l) => (mode === "farm" ? grainUnplaced : unplacedIn(l.county))));
  const offPolicy = grainUnplaced.filter((u) => !placedSet.has(u));
  const offPolicyBu = Math.round(offPolicy.reduce((a, u) => a + (Number(u.quantity) || 0), 0));

  const sum = (f) => r1(lines.reduce((a, l) => a + f(l), 0));
  const total = {
    county: "Total",
    planted: sum((l) => l.planted), appraised: sum((l) => l.appraised), combined: sum((l) => l.combined),
    chopped: sum((l) => l.chopped), nothing: sum((l) => l.nothing),
    appraisedUnplaced: sum((l) => l.appraisedUnplaced),
    grainNothing: sum((l) => l.grainNothing),
    silage: {
      estAc: sum((l) => l.silage.estAc), noneAc: sum((l) => l.silage.noneAc), nothing: sum((l) => l.silage.nothing),
      machineAc: sum((l) => l.silage.machineAc),
      wetTons: lines.reduce((a, l) => a + l.silage.wetTons, 0),
      dmTons: lines.reduce((a, l) => a + l.silage.dmTons, 0),
      buAppr: {
        fields: lines.reduce((a, l) => a + l.silage.buAppr.fields, 0),
        acres: sum((l) => l.silage.buAppr.acres),
        bu: lines.reduce((a, l) => a + l.silage.buAppr.bu, 0),
      },
    },
    bu: {
      appraised: lines.reduce((a, l) => a + l.bu.appraised, 0),
      combined: lines.reduce((a, l) => a + l.bu.combined, 0),
      chopped: lines.reduce((a, l) => a + l.bu.chopped, 0),
      unmatched: lines.reduce((a, l) => a + l.bu.unmatched, 0),
      hmc: lines.reduce((a, l) => a + l.bu.hmc, 0),
      // Field not identified: counted in its unit's county, plus any line whose unit's county
      // is not on the policy (counted at the farm, flagged).
      appraisedUnplaced: lines.reduce((a, l) => a + l.bu.appraisedUnplaced, 0) + offPolicyBu,
      appraisedUnplacedOffPolicy: offPolicyBu,
    },
    unplacedLines: grainUnplaced.length,
    hmcAc: sum((l) => l.hmcAc),
    ppAcres: sum((l) => l.acc.policyPp),
    unmatchedAc: sum((l) => l.unmatchedAc),
    gaps: {
      neither: sum((l) => l.gaps.neither), unmapped: sum((l) => l.gaps.unmapped),
      chopped: sum((l) => l.gaps.chopped), harvestUnknown: sum((l) => l.gaps.harvestUnknown),
      noField: sum((l) => l.gaps.noField), partial: sum((l) => l.gaps.partial || 0),
    },
    apprUnusable: sum((l) => l.apprUnusable),
    overAccounted: sum((l) => l.acc.overAccounted),
  };
  // Every planted acre has a figure, silage-insured acres included (whether 026 acres waiting on
  // tons should hold the grain read is Kevin's call, audit 2026-11-03 #10 — one line here).
  // `grainComplete` / `silageComplete` say which side is still open.
  // ⚠ THE INSURED TYPE DECIDES (Kevin, 2026-11-03: "It all depends on how the acres are insured").
  // `complete` is the GRAIN read: every grain-insured planted acre has a grain figure. Silage-
  // insured (026) acres still waiting on tons are the silage line's own status (`silageComplete`)
  // and no longer hold the grain claim read open (audit 2026-11-03 #10). `everyAcre` is both.
  total.grainComplete = total.grainNothing <= 0.05;
  total.silageComplete = total.silage.nothing <= 0.05;
  total.complete = total.grainComplete;
  total.everyAcre = total.nothing <= 0.05;
  // planted = grain lines + silage.estAc + silage.nothing, so the grain planted acres are the rest.
  total.grainPlanted = r1(total.planted - total.silage.estAc - total.silage.nothing);
  // Production to count: combine figure + appraisal on chopped acres + appraisal on standing acres
  // + appraisal tied to no field (counted at its unit). total.bu.unmatched — appraisal on file no
  // rule could place even at a unit — is stated beside it, never added.
  // ⚠ GRAIN ONLY. The silage line (tons) is never added: it sits in total.silage.
  total.toCountBu = total.bu.combined + total.bu.chopped + total.bu.appraised + total.bu.appraisedUnplaced;
  return { mode, lines, total, farmTotals, strays };
}

/**
 * The to-do list, derived from the counts above and the field coverage — never invented.
 * Each item is { key, text }. Empty means there is nothing outstanding.
 */
function claimTodos({ view, fc, sources }) {
  const out = [];
  const t = (fc && fc.totals) || {};
  const f1 = (v) => v.toLocaleString(undefined, { maximumFractionDigits: 1 });
  const s = (n) => (n === 1 ? "" : "s");
  const hmcN = t.hmcNoAppraisal || 0, hmcAc = t.hmcNoAppraisalAcres || 0;
  if (hmcN > 0) out.push({ key: "hmcNoAppraisal", text: `${hmcN} high-moisture-corn field${s(hmcN)} (${f1(hmcAc)} ac) marked HMC with no appraisal on file — the appraisal is the figure for them. Upload it or enter the bushels.` });
  if (t.chopped - hmcN > 0) out.push({ key: "chopped", text: `${t.chopped - hmcN} field${s(t.chopped - hmcN)} (${f1(t.choppedAcres - hmcAc)} ac) chopped with no appraisal on file — on a grain policy there is no figure for them. Upload the appraisal or enter the bushels.` });
  if (t.moistureFlagged > 0) out.push({ key: "moistureFlagged", text: `${t.moistureFlagged} field${s(t.moistureFlagged)} (${f1(t.moistureFlaggedAcres)} ac) combined above 28% moisture — high-moisture corn? Mark ${t.moistureFlagged === 1 ? "it" : "them"} under Details to use the appraisal.` });
  if (t.harvestUnknown > 0) out.push({ key: "harvestUnknown", text: `${t.harvestUnknown} field${s(t.harvestUnknown)} (${f1(t.harvestUnknownAcres)} ac) harvested in a form the machine data does not state (grain or chopped) and no appraisal on file.` });
  // Silage-insured acres (026): their own items, never folded into a grain one.
  if (t.silageBuAppraisals > 0) out.push({ key: "silageBuAppraisal", text: `${t.silageBuAppraisals} silage-insured field${s(t.silageBuAppraisals)} (${f1(t.silageBuAppraisalAcres)} ac) appraised in bushels — the wrong unit for silage. Corrected appraisal needed; not counted.` });
  if (t.silageNone > 0) out.push({ key: "silageNone", text: `${t.silageNone} silage-insured field${s(t.silageNone)} (${f1(t.silageNoneAcres)} ac) have no silage figure yet (tons).` });
  if (t.neither > 0) out.push({ key: "neither", text: `${t.neither} field${s(t.neither)} (${f1(t.neitherAcres)} ac) have nothing yet — no appraisal and no combine pass.` });
  if (t.unmapped > 0) out.push({ key: "unmapped", text: `${t.unmapped} field${s(t.unmapped)} (${f1(t.unmappedAcres)} ac) were combined but the yield map was never built. Export the operation from John Deere and upload it on Field Mapper → Machine Data.` });
  if (view.total.gaps.noField > 0.05) out.push({ key: "noField", text: `${f1(view.total.gaps.noField)} planted ac on the policy have no field on the map and no appraisal to stand in for them.` });
  if (t.mixedSetAside > 0) out.push({ key: "mixedSetAside", text: `${t.mixedSetAside} field${s(t.mixedSetAside)} combined and chopped where the appraisal also covers the combined acres — appraisal used, combine set aside. Adjuster to confirm.` });
  if (t.partialFields > 0) out.push({ key: "partial", text: `${t.partialFields} field${s(t.partialFields)} partly harvested: ${f1(t.partialUncoveredAcres)} ac with no figure.` });
  const unp = ((fc && fc.unplacedAppraisals) || []).filter((u) => u.typeClass !== "silage");
  if (unp.length > 0) out.push({ key: "unplaced", text: `${unp.length} appraisal line${s(unp.length)} counted at the unit, not tied to a field (see Details).` });
  for (const u of unp) if (u.headerConflict) out.push({ key: "headerConflict", text: `Line ${u.field || "?"}: ${u.headerConflict}.` });
  const sets = ((fc && fc.farmLevelAppraisals) || []).filter((u) => u.fieldSet);
  if (sets.length) out.push({ key: "fieldSet", text: `${sets.length} multi-field line${s(sets.length)} matched to a John Deere field's fields by acres. Adjuster to confirm.` });
  const ties = ((fc && fc.rows) || []).filter((r) => r.appraised && r.appraised.confirm);
  if (ties.length) out.push({ key: "tieBroken", text: `${ties.length} line${s(ties.length)} placed on the one tied field with a harvest pass. Adjuster to confirm.` });
  const silUnp = (fc && fc.unplacedSilageBu) || [];
  if (silUnp.length) out.push({ key: "silageBuUnplaced", text: `${silUnp.length} silage-insured line${s(silUnp.length)} in bushels not tied to a field. Wrong unit; not counted.` });
  if (fc && fc.unplacedPassCount > 0) out.push({ key: "unplacedPass", text: `${fc.unplacedPassCount} combine pass${fc.unplacedPassCount === 1 ? "" : "es"} could not be tied to an insured field (see Details).` });
  if (view.total.apprUnusable > 0.05) out.push({ key: "apprUnusable", text: `${f1(view.total.apprUnusable)} ac of appraisal on file are not tied to any field and are not counted above — check the matching (see Details).` });
  if (view.total.overAccounted > 0.05) out.push({ key: "over", text: `The field map carries ${f1(view.total.overAccounted)} ac more accounted ground than were planted — an acreage finding to check against the acreage report.` });
  const ask = ((sources && sources.rows) || []).find((r) => r.present && r.needsHuman && (r.candidates || []).length);
  if (ask) out.push({ key: "recordType", text: `Pick the RMA record type (${ask.candidates.join(", ")}) for the ${String(ask.label || "production").toLowerCase()} before filing.` });
  return out;
}

// ⚠ THE ONE GRAIN COMBINE FIGURE (Kevin, 2026-11-03: "Combine is grain, choppers harvest silage
// as tons. So yes, if a field is insured as grain and we have JD yield data for grain bushels,
// use that"). It is the Claims page's own "Combined" column — fieldCoverage rows whose state is
// `harvested` or `both` (a combine pass on grain-insured or untyped ground; a chopper pass, a
// silage-insured field, an HMC-marked field and a pass of unknown form are never in it) — so the
// headline, the % of guarantee and the claim verdict (computeHarvestClaim `combine`) read one
// figure. `acres` is the ground the combine measured (capped at each field's planted acres).
// CORN and SOYBEANS (Kevin, 2026-11-03: "yes soybean claim manager should be using JD Soybean yield
// data"). A soybean pass is always grain; other crops keep the verdict they had until he says so.
// `acres` are the planted acres the combine figure stands for: the combine's measured acres, or the
// whole field where its passes covered FULL_HARVEST_SHARE of it (fieldCoverage: no remainder), or a
// mixed field's combine part.
const COMBINE_COUNTED_CROPS = Object.freeze(["CORN", "SOYBEANS"]);
function grainCombineOf(fc) {
  if (!fc || !COMBINE_COUNTED_CROPS.includes(up(fc.crop))) return null;
  const rows = (fc.rows || []).filter((r) => inStates(r, ["harvested", "both"]) && r.harvested);
  const bu = Math.round(rows.reduce((a, r) => a + qtyOf(combineFig(r)), 0));
  const covered = (r) => {
    const planted = Math.max(0, r.insuredAcres - (r.ppAcres || 0));
    if (r.combinePart) return Math.min(planted, r.combinePart.acres);
    if (r.state === "harvested" && r.stateAcres != null && !(r.uncoveredAcres > 0.005)) return Math.min(planted, r.stateAcres);
    return Math.min(planted, r.harvested.acres);
  };
  const acres = r1(rows.reduce((a, r) => a + covered(r), 0));
  return bu > 0 ? { bu, acres } : null;
}

module.exports = { claimAccountingView, claimTodos, totalsOf, grainCombineOf, COMBINE_COUNTED_CROPS, rowProduction, allocateFarmLevel };
